import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useMemo, Suspense, useEffect } from "react";
import { Funnel } from "@airhelp/funnel-core";
const FunnelContext = createContext(null);
function FunnelContextProvider({
  funnel,
  children
}) {
  return /* @__PURE__ */ jsx(FunnelContext.Provider, { value: funnel, children });
}
function useFunnel() {
  const funnel = useContext(FunnelContext);
  if (!funnel) {
    throw new Error(
      "You should call useFunnel inside FunnelContextProvider with a valid funnel provided"
    );
  }
  return funnel;
}
function StaticFunnel({ children }) {
  return /* @__PURE__ */ jsx(
    FunnelContextProvider,
    {
      funnel: new Funnel({
        steps: { list: [], record: /* @__PURE__ */ new Map() }
      }),
      children
    }
  );
}
function ReactFunnel({
  definition,
  firstStep,
  loadingIndicator
}) {
  const [activeStep, setActiveStep] = useState(null);
  const onStepChange = useCallback(
    (step) => {
      (activeStep == null ? void 0 : activeStep.name) !== (step == null ? void 0 : step.name) && setActiveStep(step);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want this to recompute with activeStep changes, because later the funnel memo would recompute, it should stay the same
    [definition, setActiveStep]
  );
  const funnel = useMemo(
    () => buildFunnel({ definition: { ...definition, onStepChange }, firstStep }),
    [definition, firstStep, onStepChange]
  );
  if (!activeStep) {
    return null;
  }
  return /* @__PURE__ */ jsx(Suspense, { fallback: loadingIndicator, children: /* @__PURE__ */ jsx(FunnelContextProvider, { funnel, children: /* @__PURE__ */ jsx(
    ReactFunnelStep,
    {
      onStepEnter: definition.onStepEnter,
      step: activeStep
    }
  ) }) });
}
function buildFunnel({
  definition,
  firstStep
}) {
  const funnel = new Funnel(definition);
  if (firstStep) {
    funnel.forceStep(firstStep);
  } else {
    const initialStep = definition.steps.list.at(0);
    if (!initialStep) {
      throw new Error(
        "Funnel load error, funnel definition should always have at least one step"
      );
    }
    funnel.forceStep(initialStep.name);
  }
  return funnel;
}
function ReactFunnelStep({
  onStepEnter,
  step
}) {
  useEffect(() => {
    onStepEnter && onStepEnter(step);
  }, []);
  if (!step.Component) {
    throw new Error(
      `Funnel load error, funnel step should always have a component! Occurred at step: ${step.name}`
    );
  }
  return /* @__PURE__ */ jsx(step.Component, {});
}
export {
  FunnelContextProvider as F,
  ReactFunnel as R,
  StaticFunnel as S,
  useFunnel as u
};
//# sourceMappingURL=react-D5l6P7RL.js.map
